import { useEffect, useState } from 'react';
import { Button, Modal, Typography } from 'antd';
import { useAppDispatch } from '@store/hooks';
import { refreshSessionAsync } from '@store/slices/authSlice';
import { useSessionCountdown } from '@hooks/useSessionCountdown';

const WARNING_THRESHOLD_MS = 5 * 60 * 1000;

function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

/**
 * 5-minute warning before the access token expires. Lets the user either
 * refresh the session or cancel — on cancel, no further popups appear in
 * this cycle and the bar continues counting down to zero. The natural
 * expiry is then handled by the existing ``SessionExpiredModal`` flow
 * via the 401 interceptor.
 */
export function SessionExpiringSoonModal() {
  const dispatch = useAppDispatch();
  const { isActive, remainingMs, expiresAt } = useSessionCountdown();
  const [dismissedFor, setDismissedFor] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Reset dismissal whenever a new session window starts (e.g. after a
  // successful refresh in this or any other tab).
  useEffect(() => {
    setDismissedFor(null);
  }, [expiresAt]);

  const open =
    isActive &&
    remainingMs > 0 &&
    remainingMs <= WARNING_THRESHOLD_MS &&
    dismissedFor !== expiresAt;

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await dispatch(refreshSessionAsync()).unwrap();
    } catch {
      /* If refresh fails the natural 401 → SessionExpiredModal flow takes over. */
    } finally {
      setRefreshing(false);
    }
  };

  const handleCancel = () => {
    setDismissedFor(expiresAt);
  };

  return (
    <Modal
      open={open}
      title="Session expiring soon"
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleCancel} disabled={refreshing}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleRefresh} loading={refreshing}>
            Refresh session
          </Button>
        </div>
      }
      transitionName="ant-fade"
      maskTransitionName="ant-fade"
      styles={{ body: { paddingTop: 8 } }}
    >
      <Typography.Paragraph style={{ marginBottom: 0 }}>
        Your session will expire in <strong>{formatRemaining(remainingMs)}</strong>.
        Refresh to keep working, or cancel to let it expire on its own.
      </Typography.Paragraph>
    </Modal>
  );
}
